/**
 * Funding utility functions for React Native admin app
 * Matches the admin-web funding helpers exactly
 */

import { 
  FundingJournalData, 
  GLDetail, 
  IFundingAmounts,
  IFundingCsvData,
  IFundingCsvRow,
  CsvData,
  CsvRow,
  isGLDetail,
  isValidFundingRow,
  IProcessedFunding,
  IFundingTableRow,
  Funding
} from '@/types/admin/funding';

// Shared CSV headers for funding exports
export const FUNDING_CSV_HEADERS: CsvRow = [
  "Property",
  "Merchant Id",
  "Funding Date",
  "Last Status",
  "Ledger Debits",
  "Ledger Credits",
  "Fee Debits",
  "Fee Credits",
  "Total Debits",
  "Total Credits",
];

// Consistent memo constants used across UI and CSV logic
export const MEMO_LEDGER = ["Ledger Amount", "Reversal Ledger", "Net Sales Other"] as const;
export const MEMO_FEE = ["Fee Amount", "Reversal Fee", "Convenience Fee"] as const;
export const MEMO_TOTAL = ["Total Funding", "Reversal Total Funding"] as const;

/**
 * Get amounts from journal data for specific memos (exact copy of admin-web logic)
 */
export const getAmounts = (journalData: FundingJournalData | null, memos: string[]): IFundingAmounts => {
  let debitAmount = 0.0;
  let creditAmount = 0.0;
  
  if (!journalData || !journalData.glDetail || journalData.glDetail.length === 0) {
    return { debitAmount, creditAmount };
  }

  // Pre-index by memo to avoid repeated .find
  const memoToEntry = new Map<string, GLDetail>();
  for (const detail of journalData.glDetail) {
    if (isGLDetail(detail) && typeof detail.memo === 'string') {
      // Only keep first occurrence per memo; if duplicates appear, first wins
      if (!memoToEntry.has(detail.memo)) memoToEntry.set(detail.memo, detail);
    }
  }

  for (const memo of memos) {
    const entry = memoToEntry.get(memo);
    if (!entry) continue;

    const parsedDebitAmount = parseFloat(entry.debitAmount);
    const parsedCreditAmount = parseFloat(entry.creditAmount);

    if (Number.isFinite(parsedDebitAmount) && parsedDebitAmount > 0.0) {
      debitAmount = parsedDebitAmount;
    }
    if (Number.isFinite(parsedCreditAmount) && parsedCreditAmount > 0.0) {
      creditAmount = parsedCreditAmount;
    }
  }
  
  return { debitAmount, creditAmount };
};

/**
 * Format CSV data for funding (matching admin-web logic exactly)
 */
export const formatFundingCsvData = (fundingData: IFundingCsvData): CsvData => {
  if (!fundingData || !fundingData.rows) return [FUNDING_CSV_HEADERS];

  const listData: CsvRow[] = fundingData.rows
    .filter(isValidFundingRow)
    .map((row: IFundingCsvRow): CsvRow => {
    const { debitAmount: ledgerDebit, creditAmount: ledgerCredit } = getAmounts(
      row.fundingJournalData,
      [...MEMO_LEDGER],
    );
    const { debitAmount: feeDebit, creditAmount: feeCredit } = getAmounts(
      row.fundingJournalData,
      [...MEMO_FEE],
    );
    const { debitAmount: totalDebit, creditAmount: totalCredit } = getAmounts(
      row.fundingJournalData,
      [...MEMO_TOTAL],
    );
    
    return [
      row.property,
      row.merchantId,
      new Date(row.fundingDate).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
      row.lastStatus,
      ledgerDebit,
      ledgerCredit,
      feeDebit,
      feeCredit,
      totalDebit,
      totalCredit,
    ];
  });

  return [FUNDING_CSV_HEADERS, ...listData];
};

/**
 * Format amount for display with proper styling info
 */
export const formatAmountDisplay = (
  debitAmount: number,
  creditAmount: number
): {
  text: string;
  color: 'green' | 'red' | 'gray';
  type: 'debit' | 'credit' | 'none';
} => {
  if (debitAmount > 0.0) {
    return {
      text: `$${debitAmount.toFixed(2)} Debited`,
      color: 'green',
      type: 'debit',
    };
  }
  
  if (creditAmount > 0.0) {
    return {
      text: `$${creditAmount.toFixed(2)} Credited`,
      color: 'red',
      type: 'credit',
    };
  }
  
  return {
    text: 'No Data',
    color: 'gray',
    type: 'none',
  };
};

/**
 * Get funding amounts for different categories
 */
export const getFundingAmounts = (journalData: FundingJournalData | null) => {
  const ledgerAmounts = getAmounts(journalData, [...MEMO_LEDGER]);
  const feeAmounts = getAmounts(journalData, [...MEMO_FEE]);
  const totalAmounts = getAmounts(journalData, [...MEMO_TOTAL]);

  return {
    ledger: ledgerAmounts,
    fee: feeAmounts,
    total: totalAmounts,
  };
};

/**
 * Convert processed funding (action response) to table row format
 */
export const processedFundingToTableRow = (processedFunding: IProcessedFunding): IFundingTableRow => {
  return {
    id: processedFunding.id,
    property: processedFunding.merchantAccount.property.marketingName,
    merchantId: processedFunding.merchantAccount.merchantId,
    fundingDate: new Date(processedFunding.fundingDate),
    lastStatus: processedFunding.lastStatus,
    funding_amount: null,
    fee_amount: null,
    net_amount: null,
    actions: processedFunding.id,
  };
};

/**
 * Convert raw Funding row (list API) to table row format
 */
export const fundingToTableRow = (row: Funding): IFundingTableRow => {
  return {
    id: row.id,
    property: row.property,
    merchantId: row.merchantId,
    fundingDate: new Date(row.fundingDate),
    lastStatus: row.lastStatus,
    funding_amount: row.fundingJournalData,
    fee_amount: row.fundingJournalData,
    net_amount: row.fundingJournalData,
    actions: row.id,
  };
};

/**
 * Determine if a table row has any downloadable funding data
 */
export const isRowDownloadable = (row: IFundingTableRow): boolean => {
  const journalData = row.funding_amount;
  if (!journalData || !journalData.glDetail || journalData.glDetail.length === 0) {
    return false;
  }
  const cats = getFundingAmounts(journalData);
  return (
    (cats.ledger.debitAmount > 0 || cats.ledger.creditAmount > 0) ||
    (cats.fee.debitAmount > 0 || cats.fee.creditAmount > 0) ||
    (cats.total.debitAmount > 0 || cats.total.creditAmount > 0)
  );
};